import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { 
  Fuel, DollarSign, FileText, TrendingUp, Calendar, 
  Building2, Users, BarChart3, Loader2, Printer, Copy, Pencil, Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import FuelFilter from './FuelFilter';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from 'recharts';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

export default function SecretariaDashboard({ secretariaId, secretariaNome, color }) {
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [combustiveisSelecionados, setCombustiveisSelecionados] = useState([]);
  const [allCombustiveis, setAllCombustiveis] = useState([]);
  const [dashboardData, setDashboardData] = useState({
    totals: { value: 0, liters: 0, count: 0 },
    abastecimentos: [],
    byPosto: [],
    byFuncionario: [],
    byCombustivel: []
  });

  // === Handlers de acao ===
  const handlePrint = async (id) => {
    try {
      const response = await api.get(`/abastecimentos/${id}/print`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch (error) {
      toast.error('Erro ao gerar impressao');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deseja excluir este abastecimento?')) return;
    try {
      await api.delete(`/abastecimentos/${id}`);
      toast.success('Abastecimento excluido!');
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir');
    }
  };

  const handleDuplicate = async (abast) => {
    try {
      const payload = {
        date: new Date().toISOString().split('T')[0],
        codigo_abastecimento: '',
        posto_id: abast.posto_id || '',
        funcionario_id: abast.funcionario_id || '',
        veiculo_id: abast.veiculo_id || null,
        invoice_number: abast.invoice_number || '',
        motor_hp: abast.motor_hp || '',
        comunidade_id: abast.comunidade_id || null,
        odometer_start: 0,
        odometer_end: 0,
        items: (abast.items || []).map(it => ({
          combustivel_id: it.combustivel_id || '',
          quantity: it.quantity || 0,
          price_per_liter: it.price_per_liter || 0,
          total_value: (it.quantity || 0) * (it.price_per_liter || 0),
        }))
      };
      await api.post('/abastecimentos', payload);
      toast.success('Abastecimento duplicado! Edite o CA na pagina principal.');
      fetchDashboardData();
    } catch (error) {
      toast.error('Erro ao duplicar');
    }
  };

  const handleEdit = (abast) => {
    // Redirect to main Abastecimentos page with edit mode
    window.location.href = `/abastecimentos?edit=${abast.id}`;
  };

  useEffect(() => {
    fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [secretariaId, startDate, endDate, combustiveisSelecionados]);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      // Buscar abastecimentos
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      
      const [abastRes, funcionariosRes, veiculosRes, postosRes, combustiveisRes] = await Promise.all([
        api.get('/abastecimentos', { params }),
        api.get('/funcionarios'),
        api.get('/veiculos'),
        api.get('/postos'),
        api.get('/combustiveis')
      ]);

      const funcionarios = funcionariosRes.data;
      const veiculos = veiculosRes.data;
      const postos = postosRes.data;
      const combustiveis = combustiveisRes.data;
      
      // Salvar combustiveis para o filtro
      setAllCombustiveis(combustiveis);
      
      // Filtrar abastecimentos pela secretaria
      const funcIdsSecretaria = funcionarios
        .filter(f => f.secretaria_id === secretariaId)
        .map(f => f.id);
      
      let abastecimentosSecretaria = abastRes.data.filter(a => 
        funcIdsSecretaria.includes(a.funcionario_id)
      );

      // Aplicar filtro de combustivel
      if (combustiveisSelecionados.length > 0) {
        abastecimentosSecretaria = abastecimentosSecretaria.filter(a => {
          if (!a.items) return false;
          return a.items.some(item => combustiveisSelecionados.includes(item.combustivel_id));
        });
      }

      // Calcular totais (considerando filtro de combustivel)
      let totals = { value: 0, liters: 0, count: abastecimentosSecretaria.length };
      if (combustiveisSelecionados.length > 0) {
        abastecimentosSecretaria.forEach(a => {
          if (a.items) {
            const itensFiltrados = a.items.filter(item => combustiveisSelecionados.includes(item.combustivel_id));
            itensFiltrados.forEach(item => {
              totals.value += (item.quantity || 0) * (item.price_per_liter || 0);
              totals.liters += item.quantity || 0;
            });
          }
        });
      } else {
        totals.value = abastecimentosSecretaria.reduce((sum, a) => sum + (a.total_value || 0), 0);
        totals.liters = abastecimentosSecretaria.reduce((sum, a) => sum + (a.total_quantity || 0), 0);
      }

      // Agrupar por posto
      const postoStats = {};
      abastecimentosSecretaria.forEach(a => {
        const posto = postos.find(p => p.id === a.posto_id);
        const postoName = posto?.name || 'Desconhecido';
        if (!postoStats[postoName]) {
          postoStats[postoName] = { name: postoName, value: 0, liters: 0, count: 0 };
        }
        postoStats[postoName].value += a.total_value || 0;
        postoStats[postoName].liters += a.total_quantity || 0;
        postoStats[postoName].count += 1;
      });

      // Agrupar por funcionario
      const funcStats = {};
      abastecimentosSecretaria.forEach(a => {
        const func = funcionarios.find(f => f.id === a.funcionario_id);
        const funcName = func?.name || 'Desconhecido';
        if (!funcStats[funcName]) {
          funcStats[funcName] = { name: funcName, value: 0, liters: 0, count: 0 };
        }
        funcStats[funcName].value += a.total_value || 0;
        funcStats[funcName].liters += a.total_quantity || 0;
        funcStats[funcName].count += 1;
      });

      // Agrupar por combustivel
      const combStats = {};
      abastecimentosSecretaria.forEach(a => {
        if (a.items) {
          a.items.forEach(item => {
            const comb = combustiveis.find(c => c.id === item.combustivel_id);
            const combName = comb?.type || item.fuel_type || 'Desconhecido';
            if (!combStats[combName]) {
              combStats[combName] = { name: combName, value: 0, liters: 0 };
            }
            combStats[combName].value += (item.quantity || 0) * (item.price_per_liter || 0);
            combStats[combName].liters += item.quantity || 0;
          });
        }
      });

      // Preparar dados detalhados com lookup
      const abastecimentosDetalhados = abastecimentosSecretaria.map(a => {
        const func = funcionarios.find(f => f.id === a.funcionario_id);
        const veic = veiculos.find(v => v.id === a.veiculo_id);
        const posto = postos.find(p => p.id === a.posto_id);
        return {
          ...a,
          funcionario_nome: func?.name || 'N/A',
          veiculo_placa: veic?.plate || 'N/A',
          posto_nome: posto?.name || 'N/A'
        };
      }).sort((a, b) => (b.numero || 0) - (a.numero || 0));

      setDashboardData({
        totals,
        abastecimentos: abastecimentosDetalhados,
        byPosto: Object.values(postoStats).sort((a, b) => b.value - a.value),
        byFuncionario: Object.values(funcStats).sort((a, b) => b.value - a.value),
        byCombustivel: Object.values(combStats).sort((a, b) => b.liters - a.liters)
      });
    } catch (error) {
      console.error('Erro ao carregar dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="animate-spin mr-2" style={{ color }} />
        <span className="text-gray-500">Carregando dashboard...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4 mt-4" data-testid={`secretaria-dashboard-${secretariaId}`}>
      {/* Filtros de Data e Combustivel */}
      <div className="p-3 bg-gray-50 rounded-lg space-y-3">
        {/* Filtro por Periodo */}
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex items-center gap-2">
            <Calendar size={16} className="text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Periodo:</span>
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-xs text-gray-500">De:</Label>
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="h-8 text-sm w-36"
            />
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-xs text-gray-500">Ate:</Label>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="h-8 text-sm w-36"
            />
          </div>
        </div>

        {/* Filtro por Combustível - Dropdown Pesquisável */}
        <div className="flex flex-wrap items-center gap-3 pt-2 border-t">
          <FuelFilter
            combustiveis={allCombustiveis}
            selecionados={combustiveisSelecionados}
            onChange={setCombustiveisSelecionados}
            placeholder="Filtrar por combustível"
            multiple={true}
            className="w-56"
          />
          {(startDate || endDate || combustiveisSelecionados.length > 0) && (
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => { setStartDate(''); setEndDate(''); setCombustiveisSelecionados([]); }}
              className="text-xs h-7 ml-auto"
            >
              Limpar filtros
            </Button>
          )}
        </div>
      </div>

      {/* Cards de Totais */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="border-l-4" style={{ borderLeftColor: color }}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 uppercase">Valor Total</p>
                <p className="text-2xl font-bold" style={{ color }}>{formatCurrency(dashboardData.totals.value)}</p>
              </div>
              <DollarSign size={32} className="opacity-20" style={{ color }} />
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-l-4" style={{ borderLeftColor: color }}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 uppercase">Total Litros</p>
                <p className="text-2xl font-bold" style={{ color }}>{formatNumber(dashboardData.totals.liters)} L</p>
              </div>
              <Fuel size={32} className="opacity-20" style={{ color }} />
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-l-4" style={{ borderLeftColor: color }}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 uppercase">Abastecimentos</p>
                <p className="text-2xl font-bold" style={{ color }}>{dashboardData.totals.count}</p>
              </div>
              <FileText size={32} className="opacity-20" style={{ color }} />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Graficos */}
      {dashboardData.totals.count > 0 && (
        <div className="grid grid-cols-2 gap-4">
          {/* Grafico por Posto */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Building2 size={16} style={{ color }} />
                Valor por Posto
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={dashboardData.byPosto} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" tickFormatter={(v) => `R$${(v/1000).toFixed(1)}k`} />
                  <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 10 }} />
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Bar dataKey="value" fill={color} radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Grafico por Combustivel */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Fuel size={16} style={{ color }} />
                Litros por Combustivel
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={dashboardData.byCombustivel}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={70}
                    paddingAngle={5}
                    dataKey="liters"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    {dashboardData.byCombustivel.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => `${formatNumber(value)} L`} />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Top Funcionarios */}
      {dashboardData.byFuncionario.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Users size={16} style={{ color }} />
              Top Funcionarios por Valor
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {dashboardData.byFuncionario.slice(0, 8).map((func, i) => (
                <div key={i} className="p-2 bg-gray-50 rounded-lg">
                  <p className="text-xs font-medium text-gray-800 truncate">{func.name}</p>
                  <p className="text-sm font-bold" style={{ color }}>{formatCurrency(func.value)}</p>
                  <p className="text-xs text-gray-500">{formatNumber(func.liters)} L • {func.count} abast.</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabela Detalhada */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <BarChart3 size={16} style={{ color }} />
            Detalhamento de Abastecimentos
          </CardTitle>
        </CardHeader>
        <CardContent>
          {dashboardData.abastecimentos.length > 0 ? (
            <div className="max-h-96 overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">CA</TableHead>
                    <TableHead className="text-xs">Data</TableHead>
                    <TableHead className="text-xs">Funcionario</TableHead>
                    <TableHead className="text-xs">Veiculo</TableHead>
                    <TableHead className="text-xs">Posto</TableHead>
                    <TableHead className="text-xs text-right">Litros</TableHead>
                    <TableHead className="text-xs text-right">Valor</TableHead>
                    <TableHead className="text-xs text-center">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {dashboardData.abastecimentos.map((abast) => (
                    <TableRow key={abast.id} className="text-xs">
                      <TableCell className="font-medium" style={{ color }}>
                        {abast.codigo_abastecimento || abast.numero || 'N/A'}
                      </TableCell>
                      <TableCell>{abast.date ? new Date(abast.date).toLocaleDateString('pt-BR') : 'N/A'}</TableCell>
                      <TableCell className="truncate max-w-[120px]">{abast.funcionario_nome}</TableCell>
                      <TableCell>{abast.veiculo_placa}</TableCell>
                      <TableCell className="truncate max-w-[100px]">{abast.posto_nome}</TableCell>
                      <TableCell className="text-right">{formatNumber(abast.total_quantity || 0)} L</TableCell>
                      <TableCell className="text-right font-medium text-green-700">
                        {formatCurrency(abast.total_value || 0)}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1 justify-center">
                          <Button size="sm" variant="ghost" onClick={() => handlePrint(abast.id)} className="h-6 w-6 p-0 text-green-600 hover:text-green-800 hover:bg-green-50" title="Imprimir" data-testid={`dash-print-${abast.id}`}>
                            <Printer size={13} />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleDuplicate(abast)} className="h-6 w-6 p-0 text-blue-600 hover:text-blue-800 hover:bg-blue-50" title="Duplicar" data-testid={`dash-duplicate-${abast.id}`}>
                            <Copy size={13} />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleEdit(abast)} className="h-6 w-6 p-0 text-amber-600 hover:text-amber-800 hover:bg-amber-50" title="Editar" data-testid={`dash-edit-${abast.id}`}>
                            <Pencil size={13} />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleDelete(abast.id)} className="h-6 w-6 p-0 text-red-600 hover:text-red-800 hover:bg-red-50" title="Excluir" data-testid={`dash-delete-${abast.id}`}>
                            <Trash2 size={13} />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <p className="text-center text-gray-400 py-8">Nenhum abastecimento encontrado para esta secretaria</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
